import type { KeyboardEvent, ReactNode } from 'react'
import { Skeleton } from './Skeleton'

export type DataTableColumn<T> = {
  key: string
  title: ReactNode
  render?: (row: T) => ReactNode
  width?: string
  /** 桌面端（≥md）单元格最小宽度，落到 th/td 的 style min-width——auto 布局下 width 只是建议值，会被压 (LeapWorldTwin-233) */
  minWidth?: string
  /** 长文本列显式声明可换行；默认桌面端单元格不换行（md:whitespace-nowrap），短列不再被压到逐字断行 (LeapWorldTwin-233) */
  wrap?: boolean
  align?: 'left' | 'right'
}

export type DataTableProps<T> = {
  columns: DataTableColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string
  loading?: boolean
  empty?: ReactNode
  onRowClick?: (row: T) => void
  ariaLabel?: string
  /**
   * 窄屏（<md）降级形态（LeapWorldUI-02，D29 移动端 A+B 档）：
   * `cards`（默认）同一 DOM 按列定义转卡片——表头隐藏、行变卡片、单元格经
   * `data-label` 生出左侧列名（兼容 prm data-label 派，也让 market 双栈可删）；
   * `scroll` 保留旧的容器横向滚动。
   */
  narrowMode?: 'cards' | 'scroll'
}

const cardsRowClasses = 'max-md:mb-3 max-md:block max-md:rounded-[var(--radius-lg)] max-md:border max-md:border-[var(--color-line)] max-md:bg-[var(--color-panel)] max-md:px-4 max-md:py-1.5 max-md:shadow-[var(--shadow-sm)]'
const cardsCellClasses = 'max-md:flex max-md:items-baseline max-md:justify-between max-md:gap-3 max-md:border-t max-md:border-[var(--color-line)] max-md:px-0 max-md:py-2 max-md:text-right max-md:first:border-t-0 max-md:before:shrink-0 max-md:before:text-xs max-md:before:font-normal max-md:before:text-[var(--color-muted)] max-md:before:content-[attr(data-label)]'

export function DataTable<T>({ columns, rows, rowKey, loading = false, empty, onRowClick, ariaLabel, narrowMode = 'cards' }: DataTableProps<T>) {
  const cards = narrowMode === 'cards'
  const onKeyDown = (row: T) => (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (onRowClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault()
      onRowClick(row)
    }
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm" aria-label={ariaLabel} aria-busy={loading || undefined}>
        <thead className={cards ? 'max-md:hidden' : undefined}>
          <tr>
            {columns.map((column) => (
              <th key={column.key} style={column.width || column.minWidth ? { width: column.width, minWidth: column.minWidth } : undefined} className={`whitespace-nowrap bg-[var(--color-soft)] px-4 py-2 text-xs font-semibold text-[var(--color-muted)] ${column.align === 'right' ? 'text-right' : 'text-left'}`}>
                {column.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading
            ? [0, 1, 2].map((row) => (
                // 骨架行与数据行同结构（tr 承担行分隔线）；骨架条复用 Skeleton，自带 motion-safe 降级
                <tr key={row} className={`border-t border-[var(--color-line)] ${cards ? cardsRowClasses : ''}`}>
                  {columns.map((column) => <td key={column.key} data-label={cards && typeof column.title === 'string' ? column.title : undefined} style={column.minWidth ? { minWidth: column.minWidth } : undefined} className={`max-md:min-w-24 px-4 py-2.5 ${column.wrap ? '' : 'md:whitespace-nowrap'} ${cards ? cardsCellClasses : ''}`}><Skeleton className="h-3.5 w-3/4" /></td>)}
                </tr>
              ))
            : rows.map((row) => (
                <tr key={rowKey(row)} className={`border-t border-[var(--color-line)] ${cards ? cardsRowClasses : ''} ${onRowClick ? 'cursor-pointer transition-colors hover:bg-[var(--color-soft)] focus-visible:bg-[var(--color-soft)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-[var(--color-focus)]' : ''}`} onClick={onRowClick ? () => onRowClick(row) : undefined} onKeyDown={onRowClick ? onKeyDown(row) : undefined} tabIndex={onRowClick ? 0 : undefined}>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      data-label={cards && typeof column.title === 'string' ? column.title : undefined}
                      style={column.minWidth ? { minWidth: column.minWidth } : undefined}
                      className={`max-md:min-w-24 px-4 py-2.5 align-middle ${column.wrap ? '' : 'md:whitespace-nowrap'} ${column.align === 'right' ? 'text-right' : ''} ${cards ? cardsCellClasses : ''}`}
                    >
                      {column.render ? column.render(row) : (row as Record<string, ReactNode>)[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
        </tbody>
      </table>
      {!loading && rows.length === 0 ? empty ?? null : null}
    </div>
  )
}
